import type { PlatformerInputSnapshot, PlatformerIntent } from './types';

const ALL_INTENTS: readonly PlatformerIntent[] = [
  'left',
  'right',
  'up',
  'down',
  'jump',
  'run',
  'start',
  'select',
];

const KEYBOARD_BINDINGS: Readonly<Record<string, PlatformerIntent>> = {
  ArrowLeft: 'left',
  KeyA: 'left',
  ArrowRight: 'right',
  KeyD: 'right',
  ArrowUp: 'up',
  KeyW: 'up',
  ArrowDown: 'down',
  KeyS: 'down',
  KeyZ: 'jump',
  KeyJ: 'jump',
  Space: 'jump',
  KeyX: 'run',
  KeyK: 'run',
  ShiftLeft: 'run',
  ShiftRight: 'run',
  Enter: 'start',
  NumpadEnter: 'start',
  Backspace: 'select',
  Tab: 'select',
};

/**
 * Aggregates keyboard and physical-console sources into gameplay intents. An
 * intent remains held until every source releases it, so keyboard and 3D GBA
 * buttons can be combined without cancelling one another.
 */
export class PlatformerInput {
  private readonly sources = new Map<PlatformerIntent, Set<string>>();
  private readonly pressed = new Set<PlatformerIntent>();
  private keyboardTarget: Window | HTMLElement | null = null;

  constructor() {
    ALL_INTENTS.forEach((intent) => this.sources.set(intent, new Set<string>()));
  }

  attachKeyboard(target: Window | HTMLElement = window): void {
    if (this.keyboardTarget === target) return;
    this.detachKeyboard();
    this.keyboardTarget = target;
    target.addEventListener('keydown', this.onKeyDown as EventListener);
    target.addEventListener('keyup', this.onKeyUp as EventListener);
    target.addEventListener('blur', this.onBlur as EventListener);
  }

  detachKeyboard(): void {
    if (!this.keyboardTarget) return;
    this.keyboardTarget.removeEventListener('keydown', this.onKeyDown as EventListener);
    this.keyboardTarget.removeEventListener('keyup', this.onKeyUp as EventListener);
    this.keyboardTarget.removeEventListener('blur', this.onBlur as EventListener);
    this.clearSourcePrefix('keyboard:');
    this.keyboardTarget = null;
  }

  setIntent(intent: PlatformerIntent, active: boolean, source = 'external'): void {
    const intentSources = this.sources.get(intent);
    if (!intentSources) return;
    const wasHeld = intentSources.size > 0;
    if (active) {
      intentSources.add(source);
      if (!wasHeld && intentSources.size > 0) this.pressed.add(intent);
    } else {
      intentSources.delete(source);
    }
  }

  pulse(intent: PlatformerIntent, source = 'pulse'): void {
    this.setIntent(intent, true, source);
    this.setIntent(intent, false, source);
  }

  isHeld(intent: PlatformerIntent): boolean {
    return (this.sources.get(intent)?.size ?? 0) > 0;
  }

  consumePressed(intent: PlatformerIntent): boolean {
    if (!this.pressed.has(intent)) return false;
    this.pressed.delete(intent);
    return true;
  }

  clearAll(): void {
    for (const intentSources of this.sources.values()) intentSources.clear();
    this.pressed.clear();
  }

  snapshot(): PlatformerInputSnapshot {
    return {
      left: this.isHeld('left'),
      right: this.isHeld('right'),
      up: this.isHeld('up'),
      down: this.isHeld('down'),
      jump: this.isHeld('jump'),
      run: this.isHeld('run'),
      start: this.isHeld('start'),
      select: this.isHeld('select'),
    };
  }

  dispose(): void {
    this.detachKeyboard();
    this.clearAll();
  }

  private clearSourcePrefix(prefix: string): void {
    for (const intentSources of this.sources.values()) {
      for (const source of intentSources) {
        if (source.startsWith(prefix)) intentSources.delete(source);
      }
    }
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    const intent = KEYBOARD_BINDINGS[event.code];
    if (!intent) return;
    event.preventDefault();
    this.setIntent(intent, true, `keyboard:${event.code}`);
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    const intent = KEYBOARD_BINDINGS[event.code];
    if (!intent) return;
    event.preventDefault();
    this.setIntent(intent, false, `keyboard:${event.code}`);
  };

  private readonly onBlur = (): void => {
    this.clearSourcePrefix('keyboard:');
  };
}
